import { Col } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { flushBuffer, trackStructEvent } from "@snowplow/browser-tracker";
import React, { useContext, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { BLOCKING_ERROR_TYPE } from "@/apis/constants";
import { Logger } from "@/apis/Logger";
import AllStepsCompleted from "@/components/AllStepsCompleted";
import AmazonPassword from "@/components/AmazonPassword";
import AmazonUsername from "@/components/AmazonUsername";
import ErrorBanner from "@/components/Commons/ErrorBanner";
import { AppNotAvailableError } from "@/components/Commons/ErrorBanner/AppNotAvailableError";
import { IneligibleError } from "@/components/Commons/ErrorBanner/IneligibleError";
import StepContainer from "@/components/Commons/StepContainer";
import ConnectVpn from "@/components/ConnectVpn";
import { DAY1_ONBOARDING_STEPS, Step, STEP_ID } from "@/components/constants";
import { ChatWithITSupportFlyoutLink } from "@/components/GetSupport";
import IdentityCheck from "@/components/IdentityCheck";
import PreCacheADMobile from "@/components/PreCacheADMobile";
import RegisterSecurityKey from "@/components/RegisterSecurityKey";
import { setStepCache } from "@/helpers/CachedStepProgress";
import { getOS } from "@/helpers/os-helper";
import { SnowplowTracker } from "@/helpers/snowplow";
import { useCheckReqs } from "@/hooks/useCheckReqs";
import { useLoadRegion } from "@/hooks/useLoadRegion";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import {
  Day1EventAction,
  Day1EventCategory,
  Day1EventLabel,
} from "@/types/snowplow-events";

const OS = getOS();

export const STEP_COMPONENT_MAP: Record<STEP_ID, React.ComponentType> = {
  [STEP_ID.IDENTITY_CHECK]: IdentityCheck,
  [STEP_ID.AMAZON_USERNAME]: AmazonUsername,
  [STEP_ID.REGISTER_SECURITY_KEY]: RegisterSecurityKey,
  [STEP_ID.CONNECT_VPN]: ConnectVpn,
  [STEP_ID.AMAZON_PASSWORD]: AmazonPassword,
  [STEP_ID.PRE_CACHE_AD_MOBILE]: PreCacheADMobile,
};

/**
 * Returns a filtered list of the steps that are relevant to display for this platform.
 * @param steps A full step list.
 */
export const getEnabledStepsForPlatform = (steps: Step[]) => {
  return steps.filter((step) => !step.platforms || step.platforms.includes(OS));
};
/**
 * Declare the constant outside the React component to avoid infinite re-rendering when the global app state is used
 * in the Home component.
 */
const ONBOARDING_STEPS: (JSX.Element | null)[] = getEnabledStepsForPlatform(
  DAY1_ONBOARDING_STEPS
).map((step) => {
  if (STEP_COMPONENT_MAP[step.id]) {
    const StepContent = STEP_COMPONENT_MAP[step.id];
    return (
      <StepContainer key={step.id} stepId={step.id}>
        <StepContent></StepContent>
      </StepContainer>
    );
  }
  void Logger.error(
    `Error in loading the step content since the ${step.id} component not set`
  );
  return null;
});

export default function HomeContent(): JSX.Element {
  const intl = useIntl();

  /** Identity the healthy region and init Amplify configuration */
  const { isError: isLoadRegionError } = useLoadRegion({});

  const {
    isFetched: isCheckReqsFetched,
    isError: isCheckReqsError,
    candidateLocalStartDate,
    blockingErrorType,
  } = useCheckReqs();
  const { state, dispatch } = useContext(AppContext);
  const allStepsCompleted = getEnabledStepsForPlatform(state.steps).every(
    (step) => step.completed
  );

  /**
   * Set the refPage to provide context to IT Support should the new hire initiate
   * a chat with support
   */
  useEffect(() => {
    if (
      blockingErrorType === BLOCKING_ERROR_TYPE.BeforeDay1 &&
      candidateLocalStartDate
    ) {
      dispatch({
        type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
        chatSupportRefPage: "ezo-blocking-error-not-day-1",
      });
    }
  }, [blockingErrorType, candidateLocalStartDate]);

  useEffect(() => {
    if (allStepsCompleted) {
      trackStructEvent({
        category: Day1EventCategory.Complete,
        action: Day1EventAction.SectionToggled,
      });
      dispatch({
        type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
        nonDirectedChatSupportContext: "ON_WIZARD_COMPLETED_STEP",
      });
      // Force all buffered events to be flushed since this is the end of the app flow.
      // Otherwise buffered events may be lost.
      flushBuffer();
    }
  }, [allStepsCompleted]);

  // Keep the cache up to date when the wizard advances.
  useEffect(() => {
    setStepCache(state.currentStepNum - 1);
  }, [state.currentStepNum]);

  useEffect(() => {
    if (isCheckReqsFetched) {
      SnowplowTracker.trackSystemInfo();
    }
  }, [isCheckReqsFetched]);

  if (
    blockingErrorType === BLOCKING_ERROR_TYPE.BeforeDay1 &&
    candidateLocalStartDate
  ) {
    return (
      <ErrorBanner>
        <FormattedMessage
          id="error-beforeStartDate"
          values={{
            chatWithITSupportLink: (
              <ChatWithITSupportFlyoutLink
                localeMessageId="text-chatWithITSupport"
                metricProps={{
                  category: Day1EventCategory.BlockingError,
                  action: Day1EventAction.ChatWithSupport,
                  label: Day1EventLabel.NotDay1,
                }}
              />
            ),
            startDate: intl.formatDate(candidateLocalStartDate.toDate(), {
              month: "long",
              day: "numeric",
            }),
          }}
        />
      </ErrorBanner>
    );
  }

  /**
   * Failed to resolve a healthy EZO API region, so EZO is unavailable.
   * Direct the new hire to use First Aid and engage their manager to
   * onboard with virtual new hire orientation (vNHO)
   */
  if (isLoadRegionError) {
    return <AppNotAvailableError />;
  }

  if (isCheckReqsError) {
    return <IneligibleError />;
  }

  if (isCheckReqsFetched) {
    return (
      <Col gridGap="S400">
        {ONBOARDING_STEPS}
        {allStepsCompleted && <AllStepsCompleted />}
      </Col>
    );
  }

  return (
    <Spinner
      dataTestId="homeContentSpinner"
      showText
      size={SpinnerSize.Medium}
    />
  );
}
